import { useState, useRef, useMemo }from 'react';
import { StaticDateRangePicker } from '@mui/x-date-pickers-pro/StaticDateRangePicker';
import { TextField, Popover } from '@mui/material';
import PropTypes from 'prop-types';
import Iconify from '../../../reusable-components/iconify';

const shortcutsItems = [
  {
    label: 'Last 7 Days',
    getValue: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 7);
      return [start, end];
    },
  },
  {
    label: 'Last 30 Days',
    getValue: () => {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - 30);
      return [start, end];
    },
  },
  {
    label: 'This Month',
    getValue: () => {
      const end = new Date();
      const start = new Date(end.getFullYear(), end.getMonth(), 1);
      return [start, end];
    },
  },
  {
    label: 'Last 6 Months',
    getValue: () => {
      const end = new Date();
      const start = new Date();
      start.setMonth(end.getMonth() - 5);
      start.setDate(1);
      return [start, end];
    },
  },
  {
    label: '1 Year',
    getValue: () => {
      const end = new Date();
      const start = new Date();
      start.setFullYear(end.getFullYear() - 1);
      start.setDate(start.getDate() + 1);
      return [start, end];
    },
  },
  {
    label: 'This Year',
    getValue: () => {
      const end = new Date();
      const start = new Date(end.getFullYear(), 0, 1);
      return [start, end];
    },
  },
  {
    label: 'All Time',
    getValue: () => {
      const end = new Date();
      const start = new Date(2020, 0, 1);
      return [start, end];
    },
  },
  { label: 'Reset', getValue: () => [null, null] },
];


DateFilter.propTypes = {
  filters: PropTypes.object,
  setFilters: PropTypes.func
}
export default function DateFilter({ filters, setFilters }) {
  const anchorRef = useRef(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  const isSameDay = (a, b) =>
    a &&
    b &&
    a.getDate() === b.getDate() &&
    a.getMonth() === b.getMonth() &&
    a.getFullYear() === b.getFullYear();

  const textFieldValue = useMemo(() => {
    const match = shortcutsItems.find((item) => {
      if (!item.getValue) return false;
      const [start, end] = item.getValue();
      return (
        filters.startDate &&
        filters.endDate &&
        start &&
        end &&
        isSameDay(start, filters.startDate) &&
        isSameDay(end, filters.endDate)
      );
    });
    if (match) return match.label;
    if(!filters.startDate && !filters.endDate) return 'Select Date Range';
    return `${filters.startDate ? filters.startDate.toLocaleDateString() : ''} - ${
      filters.endDate ? filters.endDate.toLocaleDateString() : ''
    }`;
  }, [filters.startDate, filters.endDate]);

  return (
    <>
      <TextField
        value={textFieldValue}
        inputRef={anchorRef}
        onClick={() => setIsDatePickerOpen(true)}
        readOnly
        size="small"
        sx={{
          maxWidth: '200px',
          '& .MuiInputBase-root': {
            width: 'fit-content',
          },
          '& input': {
            maxWidth: '175px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#f2f5f7',
            '& fieldset': { borderColor: '#dee4e9' },
            '&:hover fieldset': { borderColor: '#dee4e9' },
            '&.Mui-focused fieldset': { borderColor: '#dee4e9' },
          },
        }}
        InputProps={{
          endAdornment: <Iconify icon="eva:calendar-outline" />,
        }}
        autoComplete="off"
      />

      <Popover
        open={isDatePickerOpen}
        anchorEl={anchorRef.current}
        onClose={() => setIsDatePickerOpen(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: { mt: 1 },
        }}
      >
        <StaticDateRangePicker
          displayStaticWrapperAs="desktop"
          value={[filters.startDate, filters.endDate]}
          onChange={([start, end]) => {
            setFilters((prev) => ({ ...prev, startDate: start, endDate: end }));
          }}
          slotProps={{
            shortcuts: { items: shortcutsItems },
            actionBar: { actions: [] },
          }}
          calendars={2}
        />
      </Popover>
    </>
  );
}